import {
    AnimationBaseController,
    IAnimationControllerConfiguration,
    Inject, SoundManager, SoundType,
} from '@denishussar/html5-game-engine';
import {
    AppStateEnum,
    GameModel,
    MenuStateEnum,
} from '../../Models/Game/GameModel';
import { AnimationContents } from '../../../AnimationContents';
import {
    IIntroButtonControllerOptions,
    MenuScreenButtonController,
} from './MenuScreenButtonController';
import { GameLogic } from '../../Logic/GameLogic';
import { SelectDifficultyController } from './Modals/SelectDifficulty/SelectDifficultyController';
import { SelectCharacterController } from './Modals/SelectCharacter/SelectCharacterController';
import { SettingsController } from './Modals/Settings/SettingsController';
import { LeaderboardController } from './Modals/Leaderboard/LeaderboardController';
import { RulesController } from './Modals/Rules/RulesController';
import { AchievementsController } from './Modals/Achievements/AchievementsController';
import { SoundConstants } from '../../Constants/SoundConstants';

export interface IMenuControllerOptions {}

export class MenuScreenController extends AnimationBaseController {
    public $model: GameModel;

    private _menuBgWidth: number = 0;
    private _menuBgHeight: number = 0;

    public constructor(
        @Inject private _options: IMenuControllerOptions,
        @Inject private _gameLogic: GameLogic,
        @Inject private _soundManager: SoundManager
    ) {
        super('MenuController');
    }

    protected getConfiguration(): IAnimationControllerConfiguration {
        return <IAnimationControllerConfiguration>{
            animationContent: AnimationContents.START_SCREEN_JSON,
            libraryName: 'start_container',
            modelWatch: {
                appState: async (state: AppStateEnum) => {
                    if (state !== AppStateEnum.menu) {
                        const modal = this.$childControllers.get('modal');
                        if (modal) {
                            await this.removeChildControllerByNameAsync(
                                'modal'
                            );
                        }
                    }
                },
                menuState: async (state: MenuStateEnum) => {
                    const modal = this.$childControllers.get('modal');
                    if (modal) {
                        await this.removeChildControllerByNameAsync('modal');
                    }

                    this.toggleInteractive(true);

                    switch (state) {
                        case MenuStateEnum.selectDifficulty:
                            this.addChildController(
                                {
                                    view: SelectDifficultyController,
                                    model: this.$model,
                                },
                                'modal'
                            );

                            this.toggleInteractive(false);
                            break;

                        case MenuStateEnum.selectCharacter:
                            this.addChildController(
                                {
                                    view: SelectCharacterController,
                                    model: this.$model,
                                },
                                'modal'
                            );

                            this.toggleInteractive(false);
                            break;

                        case MenuStateEnum.settings:
                            this.addChildController(
                                {
                                    view: SettingsController,
                                    model: this.$model,
                                },
                                'modal'
                            );

                            this.toggleInteractive(false);
                            break;

                        case MenuStateEnum.achievements:
                            this.addChildController(
                                {
                                    view: AchievementsController,
                                    model: this.$model,
                                },
                                'modal'
                            );

                            this.toggleInteractive(false);
                            break;

                        case MenuStateEnum.leaderboards:
                            this.addChildController(
                                {
                                    view: LeaderboardController,
                                    model: this.$model,
                                },
                                'modal'
                            );

                            this.toggleInteractive(false);
                            break;

                        case MenuStateEnum.rules:
                            this.addChildController(
                                {
                                    view: RulesController,
                                    model: this.$model,
                                },
                                'modal'
                            );

                            this.toggleInteractive(false);
                            break;
                    }
                },
            },
            sounds: [
                {
                    id: SoundConstants.MENU,
                    type: SoundType.Music
                },
            ],
        };
    }

    private toggleInteractive(value: boolean): void {
        ['play', 'settings', 'leaderboard', 'rules', 'achievements'].forEach(
            (name: string) => {
                const controller =
                    this.$childControllers.get<MenuScreenButtonController>(
                        name
                    );
                if (controller) {
                    controller.display.interactive = value;
                    controller.display.interactiveChildren = value;
                }
            }
        );
    }

    protected onInitialize(): void {
        if (this._soundManager.isSoundPlaying(SoundConstants.GAME)) {
            this._soundManager.stop(SoundConstants.GAME);
        }
        if (this._soundManager.isSoundPlaying(SoundConstants.PAUSE)) {
            this._soundManager.stop(SoundConstants.PAUSE);
        }

        if (!this._soundManager.isSoundPlaying(SoundConstants.MENU)) {
            this._soundManager.play(SoundConstants.MENU, true, 0.5);
        }

        this.gotoAndPlayByQuery(
            'container',
            'menu initialization',
            false,
            false
        );

        const menuBg = this.getDisplayObjectByQuery(
            'menu_bg_container.menu_bg'
        )[0] as any;

        this._menuBgWidth = menuBg.width;
        this._menuBgHeight = menuBg.height;

        ['play', 'settings', 'leaderboard', 'rules', 'achievements'].forEach(
            (name: string) => {
                this.addChildController(
                    {
                        instanceName: `container.btn_${name}`,
                        view: MenuScreenButtonController,
                        model: name,
                        options: <IIntroButtonControllerOptions>{
                            name: name,
                            onClick: name => {
                                switch (name) {
                                    case 'play':
                                        this._gameLogic.showMenuSelectDifficulty();
                                        break;

                                    case 'settings':
                                        this._gameLogic.showMenuSettings();
                                        break;

                                    case 'achievements':
                                        this._gameLogic.showMenuAchievements();
                                        break;

                                    case 'leaderboard':
                                        this._gameLogic.showMenuLeaderboards();
                                        break;

                                    case 'rules':
                                        this._gameLogic.showMenuRules();
                                        break;
                                }
                            },
                        },
                    },
                    name
                );
            }
        );
    }

    protected onResize(width: number, height: number, scale: number): void {
        let sizeW = width / scale;
        let sizeH = height / scale;

        const menuBgScale = Math.max(
            sizeW / this._menuBgWidth,
            sizeH / this._menuBgHeight
        );

        this.setScaleByQuery(
            'menu_bg_container.menu_bg',
            menuBgScale,
            menuBgScale
        );

        const containerScale = Math.min(sizeW / 1000, sizeH / 1000);

        this.setScaleByQuery('container', containerScale, containerScale);
    }
}
